import React, { useEffect, useState } from "react";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Pressable, ScrollView, Text, useWindowDimensions, View } from "react-native";
import {
  Activity,
  BarChart3,
  Boxes,
  ChevronLeft,
  ChevronRight,
  Home,
  LogOut,
  Receipt,
  Settings,
  Tags,
  UserCog,
  UserRound,
  Users
} from "lucide-react-native";
import { getRoleLabel, normalizeRole } from "./src/utils/format";
import { LoginScreen } from "./src/screens/LoginScreen";
import { DashboardScreen } from "./src/screens/DashboardScreen";
import { ClientsScreen } from "./src/screens/ClientsScreen";
import { ProductsScreen } from "./src/screens/ProductsScreen";
import { CategoriesScreen } from "./src/screens/CategoriesScreen";
import { QuotationFormScreen } from "./src/screens/QuotationFormScreen";
import { QuotationsScreen } from "./src/screens/QuotationsScreen";
import { SettingsScreen } from "./src/screens/SettingsScreen";
import { InvoicesScreen } from "./src/screens/InvoicesScreen";
import { UsersScreen } from "./src/screens/UsersScreen";
import { LogsScreen } from "./src/screens/LogsScreen";
import { ProfileScreen } from "./src/screens/ProfileScreen";
import { ErrorBoundary, YNSLogo } from "./src/components/UI";
import { colors, styles } from "./src/styles";

const nav = [
  ["dashboard", "Dashboard", Home, ["superadmin", "admin", "manager", "sales"]],
  ["quotations", "Quotations", BarChart3, ["superadmin", "admin", "manager", "sales"]],
  ["newQuotation", "New Quotation", BarChart3, ["superadmin", "admin", "manager"]],
  ["invoice", "Invoices", Receipt, ["superadmin", "admin", "manager", "sales"]],
  ["products", "Products", Boxes, ["superadmin", "admin", "manager"]],
  ["clients", "Clients", Users, ["superadmin", "admin", "manager"]],
  ["categories", "Categories", Tags, ["superadmin", "admin", "manager"]],
  ["users", "Users", UserCog, ["superadmin", "admin"]],
  ["logs", "Activity Logs", Activity, ["superadmin", "admin"]],
  ["settings", "Company Settings", Settings, ["superadmin", "admin"]],
  ["profile", "My Profile", UserRound, ["superadmin", "admin", "manager", "sales"]]
];

export default function App() {
  const [user, setUser] = useState(null);
  const [screen, setScreen] = useState("dashboard");
  const [editingQuotationId, setEditingQuotationId] = useState(null);
  const [activeInvoiceId, setActiveInvoiceId] = useState(null);
  const [initialQuotationId, setInitialQuotationId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const { width } = useWindowDimensions();
  const wide = width >= 992;

  useEffect(() => {
    AsyncStorage.getItem("auth_user").then((stored) => stored && setUser(JSON.parse(stored)));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPopState = (event) => {
      const state = event.state || {};
      setScreen(state.screen || "dashboard");
      setEditingQuotationId(state.editingQuotationId || null);
      setActiveInvoiceId(state.activeInvoiceId || null);
      setInitialQuotationId(state.initialQuotationId || null);
    };
    window.addEventListener("popstate", onPopState);
    if (!window.history.state) window.history.replaceState({ screen, editingQuotationId, activeInvoiceId, initialQuotationId }, "", window.location.href);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  if (!user) return <LoginScreen onLogin={setUser} />;

  const logout = async () => {
    await AsyncStorage.multiRemove(["auth_token", "auth_user"]);
    setUser(null);
  };

  const go = (target, param = null) => {
    if (target === "newQuotation") {
      setEditingQuotationId(param);
      setActiveInvoiceId(null);
      setInitialQuotationId(null);
    } else if (target === "invoice") {
      if (typeof param === "object" && param !== null) {
        setActiveInvoiceId(param.invoiceId || null);
        setInitialQuotationId(param.quotationId || null);
      } else {
        setActiveInvoiceId(param);
        setInitialQuotationId(null);
      }
      setEditingQuotationId(null);
    } else {
      setEditingQuotationId(null);
      setActiveInvoiceId(null);
      setInitialQuotationId(null);
    }
    setScreen(target);
    if (typeof window !== "undefined") {
      window.history.pushState({ screen: target, editingQuotationId: param, activeInvoiceId: param }, "", window.location.href);
    }
  };
  const role = normalizeRole(user.role);
  const visibleNav = nav.filter(([, , , roles]) => roles.includes(role));

  const content = {
    dashboard: <DashboardScreen navigate={go} />,
    users: <UsersScreen user={user} />,
    clients: <ClientsScreen user={user} />,
    products: <ProductsScreen user={user} />,
    categories: <CategoriesScreen user={user} />,
    newQuotation: <QuotationFormScreen id={editingQuotationId} user={user} onSaved={() => go("quotations")} />,
    quotations: (
      <QuotationsScreen
        user={user}
        onNew={() => go("newQuotation")}
        onEdit={(id) => {
          console.log("[Quotation Edit] Edit button clicked quotation ID", id);
          console.log("[Quotation Edit] Navigate path", "newQuotation", { id });
          go("newQuotation", id);
        }}
        onViewInvoice={(invoiceId) => {
          go("invoice", { invoiceId });
        }}
        onGenerateInvoice={(quotationId) => {
          go("invoice", { quotationId });
        }}
      />
    ),
    settings: <SettingsScreen user={user} />,
    logs: <LogsScreen user={user} />,
    profile: <ProfileScreen user={user} />,
    invoice: (
      <InvoicesScreen
        user={user}
        navigate={go}
        initialInvoiceId={activeInvoiceId}
        initialQuotationId={initialQuotationId}
      />
    )
  }[screen];

  const isCollapsed = wide && collapsed;
  const sidebarWidth = wide ? (isCollapsed ? 68 : 240) : "100%";

  return (
    <View style={[styles.app, wide && { flexDirection: "row" }]}>
      <View style={[sidebarStyle, { width: sidebarWidth }, !wide && topbarStyle]}>
        {/* Sidebar Header with Brand & Collapse Toggle */}
        <View style={[sidebarHeader, isCollapsed && { justifyContent: "center", paddingHorizontal: 0 }]}>
          {!isCollapsed ? (
            <YNSLogo width={160} height={36} />
          ) : (
            <YNSLogo width={40} height={32} />
          )}

          {wide ? (
            <Pressable
              onPress={() => setCollapsed(!collapsed)}
              style={collapseToggleBtn}
              title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isCollapsed ? <ChevronRight size={16} color={colors.text} /> : <ChevronLeft size={16} color={colors.text} />}
            </Pressable>
          ) : null}
        </View>

        {/* User Badge / Profile Info */}
        {!isCollapsed ? (
          <View style={userBadgeCard}>
            <Text style={userNameText} numberOfLines={1}>{user.name || user.full_name || user.username}</Text>
            <View style={[roleTag, role === "superadmin" && { backgroundColor: "#fee2e2" }]}>
              <Text style={[roleTagText, role === "superadmin" && { color: "#dc2626" }]}>{getRoleLabel(role).toUpperCase()}</Text>
            </View>
          </View>
        ) : (
          <View style={userBadgeCompact} title={`${user.name || user.username} (${role})`}>
            <UserRound size={16} color={colors.primary} />
          </View>
        )}

        {/* Navigation Items */}
        <ScrollView horizontal={!wide} contentContainerStyle={[navWrap, !wide && { flexDirection: "row" }]}>
          {visibleNav.map(([key, label, Icon]) => {
            const active = screen === key;
            return (
              <Pressable
                key={key}
                onPress={() => go(key)}
                style={[
                  navItem,
                  active && activeNav,
                  isCollapsed && { justifyContent: "center", paddingHorizontal: 0 }
                ]}
                title={isCollapsed ? label : undefined}
              >
                <Icon size={16} color={active ? "#FFFFFF" : colors.muted} />
                {!isCollapsed ? (
                  <Text style={[navText, active && { color: "#FFFFFF" }]}>{label}</Text>
                ) : null}
              </Pressable>
            );
          })}
        </ScrollView>

        {/* Sign Out Button */}
        <Pressable
          onPress={logout}
          style={[
            logoutButton,
            isCollapsed && { justifyContent: "center", paddingHorizontal: 0 }
          ]}
          title={isCollapsed ? "Sign Out" : undefined}
        >
          <LogOut size={16} color={colors.danger} />
          {!isCollapsed ? (
            <Text style={[navText, { color: colors.danger }]}>Sign Out</Text>
          ) : null}
        </Pressable>
      </View>

      <ScrollView
        style={{ flex: 1, backgroundColor: colors.bg, width: "100%", height: "100%" }}
        contentContainerStyle={{ minHeight: "100%", paddingBottom: 40 }}
      >
        <ErrorBoundary resetKey={`${screen}-${editingQuotationId || ""}`}>
          {content}
        </ErrorBoundary>
      </ScrollView>
    </View>
  );
}

const sidebarStyle = {
  backgroundColor: colors.surface,
  borderRightWidth: 1,
  borderRightColor: colors.border,
  padding: 12,
  gap: 12
};

const topbarStyle = {
  width: "100%",
  borderRightWidth: 0,
  borderBottomWidth: 1,
  borderBottomColor: colors.border
};

const sidebarHeader = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: 8,
  borderBottomWidth: 1,
  borderBottomColor: colors.border
};

const collapseToggleBtn = {
  width: 26,
  height: 26,
  borderRadius: 6,
  borderWidth: 1,
  borderColor: colors.border,
  backgroundColor: colors.secondaryBg,
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer"
};

const navWrap = { gap: 4 };

const navItem = {
  paddingVertical: 9,
  paddingHorizontal: 12,
  borderRadius: 8,
  flexDirection: "row",
  gap: 10,
  alignItems: "center",
  cursor: "pointer"
};

const activeNav = { backgroundColor: colors.primary };

const navText = { color: colors.text, fontWeight: "600", fontSize: 13 };

const userBadgeCard = {
  backgroundColor: colors.bg,
  padding: 8,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: colors.border,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 6
};

const userBadgeCompact = {
  backgroundColor: colors.primaryLight,
  width: 36,
  height: 36,
  borderRadius: 8,
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "center"
};

const userNameText = { color: colors.text, fontSize: 12, fontWeight: "700", flex: 1 };

const roleTag = { backgroundColor: colors.primaryLight, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 };

const roleTagText = { color: colors.primary, fontSize: 9, fontWeight: "800" };

const logoutButton = {
  marginTop: "auto",
  flexDirection: "row",
  gap: 10,
  alignItems: "center",
  padding: 10,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: colors.dangerBorder,
  backgroundColor: colors.dangerLight,
  cursor: "pointer"
};
